import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { graphql } from "gatsby";
import Layout from "../components/Layout";
import Content, { HTMLContent } from "../components/Content";
import { getHrefLang } from "../common/hrefLang";

export const BlogPostTemplate = ({
  content,
  image,
  altText,
  contentComponent,
  title,
  heading,
  helmet,
}) => {
  const PostContent = contentComponent || Content;

  return (
    <section className="section">
      {helmet || ""}
      <div className="container">
        <div className="columns">
          <div className="column is-8 is-offset-2">
            <h1 className="title is-size-2 has-text-weight-bold is-bold-light">
              {title}
            </h1>
            <figure className="image ">
              <img src={image} alt={altText} />
            </figure>
            <h2 className="title is-size-2 has-text-weight-bold is-bold-light">
              {heading}
            </h2>
            <PostContent className="content" content={content} />
          </div>
        </div>
      </div>
    </section>
  );
};

BlogPostTemplate.propTypes = {
  content: PropTypes.node.isRequired,
  image: PropTypes.string,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  heading: PropTypes.string,
  altText: PropTypes.string,
  helmet: PropTypes.object,
};

const BlogPost = ({ data, location }) => {
  const { post, hreflangEdges } = data;
  const {
    title,
    heading,
    language,
    metatitle,
    metadescription,
    featuredImage,
    altText,
  } = post.frontmatter;

  const hreflangs = hreflangEdges.edges
    .map((edge) => edge.node.frontmatter)
    .filter(({ language: edgeLanguage }) => edgeLanguage !== language)
    .map((edgePost) => getHrefLang(edgePost));

  return (
    <Layout hreflangs={hreflangs} location={location} language={language}>
      <BlogPostTemplate
        location={location}
        image={featuredImage}
        content={post.html}
        contentComponent={HTMLContent}
        altText={altText}
        title={title}
        heading={heading}
        helmet={
          <Helmet titleTemplate="%s | easyFerry">
            <title>{`${title}`}</title>
            <meta name="title" content={title} />
            <meta name="description" content={`${metadescription}`} />
            <meta property="og:title" content={metatitle} />
            <meta property="og:description" content={metadescription} />
          </Helmet>
        }
      />
    </Layout>
  );
};

BlogPost.propTypes = {
  location: PropTypes.object,
  data: PropTypes.shape({
    post: PropTypes.object,
    hreflangEdges: PropTypes.object,
  }),
};

export default BlogPost;

export const pageQuery = graphql`
  query BlogPostByID(
    $id: String!
    $userDefinedSlug: String!
    $category: String!
  ) {
    post: markdownRemark(id: { eq: $id }) {
      id
      html
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        title
        heading
        metatitle
        metadescription
        language
        userDefinedSlug
        category
        featuredImage
        altText
      }
    }
    hreflangEdges: allMarkdownRemark(
      filter: {
        frontmatter: {
          userDefinedSlug: { eq: $userDefinedSlug }
          category: { eq: $category }
        }
      }
    ) {
      edges {
        node {
          frontmatter {
            userDefinedSlug
            category
            language
          }
        }
      }
    }
  }
`;
